/* ============================================================
   Jadual (schedule template) — logik masa sahaja, tiada UI.
   Dikongsi oleh /workspace-v3.9 dan modal Schedule template.

   Satu jadual ialah PERATURAN masa — bukan senarai produk. Ia tidak
   memegang kad; kad dipilih pada masa Run. Sebab itu jadual yang sama
   boleh dipakai berulang kali, dan browser lain boleh menggunakannya
   tanpa apa-apa penyelarasan antara browser.

   Pilihan set duduk DI DALAM jadual, bukan di sebelahnya. Itulah yang
   membolehkan corak "set 1 ke browser 1, set 2 ke browser 2": dua
   jadual, tetapan masa serupa, cuma set berbeza.
   ============================================================ */

/* Langkah untuk butang "Isi automatik" sahaja.

   ⚠️ Ini BUKAN lagi mod jadual. Dahulu template mempunyai dua cara yang
      bertentangan — 'selang' (peraturan) dan 'slot' (senarai waktu) — dan
      pengguna terpaksa memilih antara dua model fikiran untuk satu hasil
      yang sama. Sekarang senarai waktu ialah SATU-SATUNYA sumber kebenaran;
      selang cuma alat yang MENGISI senarai itu. Lihat `isiSelang`. */
export const INTERVALS = [
    { value: '5', label: '5 minit' },
    { value: '10', label: '10 minit' },
    { value: '15', label: '15 minit' },
    { value: '30', label: '30 minit' },
    { value: '45', label: '45 minit' },
    { value: '60', label: '1 jam' },
    { value: '120', label: '2 jam' },
];

/* Langkah minit terkecil yang boleh dijadualkan.

   ⛔ Ia BUKAN pilihan reka bentuk kita — ia had TikTok. Automasi menetapkan
      waktu dengan mengklik nombor minit di dalam roda TikTok, dicari mengikut
      teks yang TEPAT (lihat `tiktok-automation.js`, bahagian "Find minute").
      Kalau nombor itu tiada dalam roda, skrip hanya melog "Minute NOT found!"
      dan minit itu TIDAK ter-set — video naik pada waktu yang salah tanpa
      sebarang amaran kepada pengguna. Jadi setiap waktu yang kita benarkan
      pengguna taip mesti jatuh pada kelipatan ini. */
export const LANGKAH_MINIT = 5;

/* Pilihan yang dipaparkan oleh pemilih waktu (lihat PilihWaktuV39).

   Minit disenaraikan, bukan ditapis selepas ditaip. Bezanya bukan kecil:
   kotak jam browser membenarkan pengguna MENAIP 08:07, jadi satu-satunya
   perlindungan yang tinggal ialah membetulkannya kemudian — dan pembetulan
   itu sampai kepada pengguna sebagai teguran atas kerja yang dia sangka
   betul. Minit yang tiada dalam senarai tidak perlu ditegur. */
export const JAM_PILIHAN = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'));

export const MINIT_PILIHAN = Array.from(
    { length: 60 / LANGKAH_MINIT },
    (_, i) => String(i * LANGKAH_MINIT).padStart(2, '0'),
);

export const SLOT_PILIHAN = [
    '06:00', '08:00', '10:00', '12:00', '14:00',
    '16:00', '18:00', '20:00', '22:00', '00:00',
];

/* Nama hari dan bulan dalam English (arahan user 2026-08-09) — terma
   kalendar dikekalkan English seperti terma teknikal yang lain (run,
   generate, set, prompt). Ayat penerangan di sekelilingnya kekal BM.

   Dinamakan `_LABEL`, bukan `_MS`, kerana akhiran itu bermaksud Melayu
   dan akan menipu pembaca kod selepas ini. */
export const BULAN_LABEL = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const HARI_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/* TikTok hanya menerima jadual sehingga 30 hari ke hadapan. Lebih baik
   pengguna nampak amaran ini semasa menyusun daripada mendapat separuh
   video gagal semasa Run.

   Angka ini DIUKUR pada akaun sebenar (2026-08-14), bukan dianggar: pada
   14 Ogos, kalendar TikTok masih menerima 13 September, manakala seluruh
   Oktober tiada satu pun tarikh yang sah. Ia pernah ditetapkan 10 — had
   itu terlalu ketat dan menolak jadual yang TikTok sendiri terima. */
export const HAD_HARI_TIKTOK = 30;

export const jamKeMinit = (hhmm) => {
    const [h, m] = (hhmm || '00:00').split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
};

export const minitKeJam = (t) => {
    const h = Math.floor(t / 60) % 24;
    const m = t % 60;
    return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
};

/* Melaraskan satu waktu ke kelipatan yang TikTok terima.

   Ia BUKAN lagi laluan utama — pemilih waktu tidak menawarkan minit yang
   salah, jadi tiada apa yang sampai ke sini untuk dibetulkan semasa
   pengguna menaip. Ia kekal sebagai jaring untuk waktu yang MASUK dari
   tempat lain: jadual yang disimpan sebelum pemilih itu wujud, dan nilai
   yang dibaca semula daripada storan. Lihat nota LANGKAH_MINIT. */
export const laraskanMinit = (t) => (Math.round((Number(t) || 0) / LANGKAH_MINIT) * LANGKAH_MINIT) % (24 * 60);

export const laraskanJam = (hhmm) => minitKeJam(laraskanMinit(jamKeMinit(hhmm)));

/* Cadangan waktu apabila pengguna membuka "Tambah waktu": satu jam
   selepas waktu terakhir yang sudah ada. Ia hampir tidak pernah
   bertembung dengan waktu sedia ada, jadi pengguna yang menambah
   beberapa waktu berturut-turut tidak terpaksa melaraskan dua senarai
   setiap kali — dan tidak menerima "sudah ada dalam jadual ini" pada
   saat dia baru sahaja membuka pemilih. */
export const cadangWaktuBaharu = (minitSedia = []) => (
    minitSedia.length ? minitKeJam(laraskanMinit(Math.max(...minitSedia) + 60)) : '09:00'
);

/* Tengah malam hari ini — titik rujukan untuk semua offset hari. */
export const mulaHariIni = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

export const tarikhDari = (offsetHari) => {
    const d = mulaHariIni();
    d.setDate(d.getDate() + offsetHari);
    return d;
};

/* Tarikh dikira semasa Run, bukan disimpan di dalam jadual. Jadual yang
   memegang tarikh tetap ("8 Ogos") akan basi minggu depan dan pengguna
   terpaksa mengeditnya setiap kali — maka ia bukan template lagi. */
export const tarikhLabel = (offsetHari) => {
    const d = tarikhDari(offsetHari);
    return d.getDate() + ' ' + BULAN_LABEL[d.getMonth()];
};

export const tarikhPanjang = (offsetHari) => {
    const d = tarikhDari(offsetHari);
    return HARI_LABEL[d.getDay()] + ' ' + d.getDate() + ' ' + BULAN_LABEL[d.getMonth()];
};

/* ============================================================
   ⭐ TARIKH SEBENAR — kunci hari dalam KALENDAR (2026-09-16)

   ⛔ SEBAB SEKSYEN INI WUJUD, jangan patah balik tanpa membaca:
   Kalendar dahulu menyimpan kedudukan sebagai OFFSET hari (0 = hari ini,
   1 = esok). Offset itu dikira SEKALI semasa halaman dibuka dan tidak
   pernah dikira semula. Pelanggan membiarkan browser terbuka melepasi
   tengah malam, jadi "esok" secara senyap bertukar makna: kerja yang
   disusun untuk 15 Sep kekal pada offset 1, dan offset 1 kini bermaksud
   16 Sep. Tajuk lajur bertukar (ia membaca jam sebenar), isi slot tidak.
   Lebih teruk, simpanan seterusnya menukar offset itu kepada tarikh guna
   HARI INI — jadi seluruh kalendar ditulis semula satu hari ke depan dan
   rekod asalnya dipadam. Run selepas tengah malam pula menghantar video
   ke TikTok pada tarikh yang salah, lewat sehari. (Aduan pelanggan
   2026-09-14/16; diukur pada data sebenar: 109 daripada 168 akaun
   kalendar membawa kesan geseran ini.)

   Maka kunci hari kalendar kini ialah TARIKH SEBENAR 'YYYY-MM-DD'.
   Tiada baseline, tiada apa yang boleh bergeser bila jam melepasi
   tengah malam.

   ⚠️ Ia BUKAN pengganti offset di mana-mana. Template (`mulaHari`,
      `letakMasa`, `binaJadual`, `tarikhLabel` di atas) memang PERATURAN
      relatif — "mula esok" mesti kekal bermaksud esok pada hari ia
      dijalankan. Yang bertukar hanyalah KALENDAR: `hariTemplate`,
      `isian`, `waktuTambahan`, `kunciSlot`, `slotKosongDari`.
   ============================================================ */

const pad2 = (n) => String(n).padStart(2, '0');

/* Date → 'YYYY-MM-DD' waktu TEMPATAN. ⛔ JANGAN guna toISOString(): ia
   menukar ke UTC, jadi tengah malam di Malaysia jatuh ke tarikh semalam. */
export const isoTarikh = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/* 'YYYY-MM-DD' → Date tengah malam tempatan. */
export const tarikhKeDate = (tarikh) => {
    const [y, m, d] = String(tarikh || '').split('-').map(Number);
    if (!y || !m || !d) return mulaHariIni();
    return new Date(y, m - 1, d);
};

export const tarikhHariIni = () => isoTarikh(mulaHariIni());

/* Tarikh + n hari. setDate() sendiri yang melompat hujung bulan/tahun. */
export const tarikhTambah = (tarikh, n) => {
    const d = tarikhKeDate(tarikh);
    d.setDate(d.getDate() + Number(n || 0));
    return isoTarikh(d);
};

/* Berapa hari tarikh ini daripada HARI INI (0 = hari ini, 1 = esok, -1 =
   semalam). Dikira melalui Date tempatan, bukan tolak milisaat mentah —
   itu satu-satunya cara yang betul merentas hujung bulan. */
export const bezaHariIni = (tarikh) => Math.round(
    (tarikhKeDate(tarikh).setHours(0, 0, 0, 0) - mulaHariIni().getTime()) / 86400000,
);

/* Label pendek/panjang untuk KALENDAR (terima tarikh sebenar). Kembar
   `tarikhLabel`/`tarikhPanjang` di atas kekal untuk pratonton template
   yang memang bekerja dalam offset. */
export const tarikhLabelHari = (tarikh) => {
    const d = tarikhKeDate(tarikh);
    return d.getDate() + ' ' + BULAN_LABEL[d.getMonth()];
};

export const tarikhPanjangHari = (tarikh) => {
    const d = tarikhKeDate(tarikh);
    return HARI_LABEL[d.getDay()] + ' ' + d.getDate() + ' ' + BULAN_LABEL[d.getMonth()];
};

/* Kunci hari kalendar yang sah? Dipakai untuk mengenal data LAMA
   (kunci offset seperti "1" atau "-3") daripada data baharu. */
export const adalahTarikh = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v));

/* Susunan kalendar bentuk LAMA (kunci offset) → bentuk tarikh sebenar.

   Dipakai pada SATU titik sahaja: draf yang dibaca semula daripada storan
   browser. Data server sudah pun berkunci tarikh sebenar, jadi ia masuk
   tanpa penukaran (lihat jadualService).

   ⚠️ `asas` = tarikh yang offset itu dikira daripadanya. Draf lama tidak
      menyimpan tarikh ia ditulis, jadi lalainya HARI INI — sama seperti
      kelakuan lama, tidak lebih buruk. Draf yang ditulis selepas ini
      membawa kunci tarikh, jadi ia tidak melalui laluan ini langsung. */
export function naikTarafSusunanTarikh(susunanAkaun, asas = tarikhHariIni()) {
    if (!susunanAkaun || typeof susunanAkaun !== 'object') return { hariTemplate: {}, isian: {}, waktuTambahan: {} };
    const keHari = (k) => {
        if (adalahTarikh(k)) return k;
        const n = Number(k);
        return Number.isFinite(n) ? tarikhTambah(asas, n) : null;
    };
    const hariTemplate = {};
    for (const k in (susunanAkaun.hariTemplate || {})) {
        const h = keHari(k);
        if (h) hariTemplate[h] = susunanAkaun.hariTemplate[k];
    }
    const waktuTambahan = {};
    for (const k in (susunanAkaun.waktuTambahan || {})) {
        const h = keHari(k);
        if (h) waktuTambahan[h] = susunanAkaun.waktuTambahan[k];
    }
    const isian = {};
    for (const k in (susunanAkaun.isian || {})) {
        const pisah = String(k).split('_');
        const minit = pisah.pop();
        const h = keHari(pisah.join('_'));
        if (h) isian[kunciSlot(h, minit)] = susunanAkaun.isian[k];
    }
    return { ...susunanAkaun, hariTemplate, isian, waktuTambahan };
}

export const naikTarafSemuaSusunan = (susunan, asas) => {
    const keluar = {};
    for (const akaunId in (susunan || {})) keluar[akaunId] = naikTarafSusunanTarikh(susunan[akaunId], asas);
    return keluar;
};

/* ⭐ BENTUK JADUAL (dipermudah 2026-08-14, arahan user)

     { id, nama, sets, turutan, slot: ['HH:MM', …], mulaHari }

   `slot` ialah SATU-SATUNYA sumber waktu. Medan lama `corak`, `selang`,
   `mulaJam` dan `hentiJam` sudah tiada — lihat `naikTarafJadual` untuk
   jadual lama yang masih membawanya.

   ⛔ Jangan kembalikan mod 'selang'. Ia memaksa pengguna memilih antara
      dua model fikiran ("peraturan" lawan "senarai") untuk satu hasil yang
      sama, dan kalendar tarikh yang mengiringinya membuatkan template
      kelihatan seperti ia memegang tarikh — sedangkan tarikh SENTIASA
      dikira semasa Run. Kelajuan mengisi dijaga oleh butang "Isi
      automatik", bukan oleh mod berasingan. */
export const JADUAL_CONTOH = [
    {
        id: 'j1', nama: 'Set 1 — 3 slot sehari',
        sets: ['set1'], turutan: 'set',
        slot: ['08:00', '12:00', '20:00'],
        mulaHari: 1,
    },
    {
        id: 'j2', nama: 'Semua set — 3 slot sehari',
        sets: ['set1', 'set2', 'set3'], turutan: 'set',
        slot: ['08:00', '12:00', '20:00'],
        mulaHari: 1,
    },
    {
        id: 'j3', nama: 'Semua set — 5 slot sehari',
        sets: ['set1', 'set2', 'set3'], turutan: 'kad',
        slot: ['08:00', '10:00', '14:00', '18:00', '21:00'],
        mulaHari: 1,
    },
];

export const jadualBaharu = () => {
    const asas = {
        id: 'j' + Date.now(),
        sets: ['set1'], turutan: 'set',
        slot: ['08:00', '12:00', '20:00'],
        mulaHari: 1,
    };
    asas.nama = namaAuto(asas);
    return asas;
};

/* Waktu jadual sebagai MINIT, disusun dan tanpa pendua. Semua pengiraan
   masa membaca melalui pintu ini supaya tiada satu pun tempat terlupa
   menyusun atau membuang pendua. */
export function waktuJadual(j) {
    if (!j || !Array.isArray(j.slot) || !j.slot.length) return [];
    return [...new Set(j.slot.map(jamKeMinit))].sort((a, b) => a - b);
}

/* Menjana senarai waktu daripada satu peraturan selang — inilah isi butang
   "Isi automatik". Hasilnya ditulis terus ke dalam `slot`, jadi selepas
   ditekan pengguna boleh membuang atau menambah waktu satu per satu; tiada
   apa yang kekal "terikat" pada peraturan itu. */
export function isiSelang(mulaJam = '08:00', hentiJam = '23:00', selang = '30') {
    /* ⚠️ Titik MULA dilaraskan, bukan hanya langkahnya. Langkah yang
       dilaraskan menjaga JARAK antara waktu, bukan tempat senarai itu
       bermula — jadi "dari 08:07 setiap 30 minit" dahulu menghasilkan
       08:07 · 08:37 · 09:07 …, iaitu SELURUH senarai jatuh pada minit
       yang tiada dalam roda TikTok. Automasi tidak menjumpai nombor itu,
       ia hanya melog "Minute NOT found!" dan video naik pada waktu yang
       salah tanpa sebarang amaran (lihat LANGKAH_MINIT). */
    const mula = laraskanMinit(jamKeMinit(mulaJam));
    const henti = laraskanMinit(jamKeMinit(hentiJam));
    const hujung = henti > mula ? henti : 24 * 60 - 1;
    const langkah = Math.max(
        LANGKAH_MINIT,
        Math.round((Number(selang) || 30) / LANGKAH_MINIT) * LANGKAH_MINIT,
    );

    const keluar = [];
    for (let t = mula; t <= hujung && keluar.length < HAD_SLOT_SEHARI; t += langkah) {
        keluar.push(minitKeJam(t));
    }
    return keluar;
}

/* Jadual lama (bentuk 'corak') → bentuk baharu. Peraturan selang
   dikembangkan sekali menjadi senarai waktu yang setara, jadi jadual yang
   sudah disimpan pengguna terus berkelakuan SAMA seperti sebelum ini.

   Ia juga MELARASKAN setiap waktu, walaupun jadual itu sudah berbentuk
   baharu. Jadual yang disimpan sebelum pemilih 5-minit wujud boleh
   membawa minit ganjil — "Isi automatik" dahulu tidak melaraskan titik
   mulanya (lihat `isiSelang`), jadi satu tetapan tersilap sudah cukup
   untuk menghasilkan 31 waktu yang kesemuanya di luar roda TikTok.
   Membetulkannya di sini bermakna pengguna tidak perlu tahu ia pernah
   berlaku; membiarkannya bermakna dia mendapat video pada waktu yang
   salah tanpa satu pun amaran.

   Dipanggil pada titik jadual dimuatkan (lihat WorkspaceView), bukan di
   dalam setiap fungsi pengiraan — menormalkan berulang kali pada setiap
   render hanya menyembunyikan tempat yang terlupa memanggilnya. */
export function naikTarafJadual(j) {
    if (!j || typeof j !== 'object') return j;

    const bentukLama = 'corak' in j;
    const slot = bentukLama && j.corak === 'selang'
        ? isiSelang(j.mulaJam, j.hentiJam, j.selang)
        : [...(j.slot || [])];

    const baru = { ...j };
    if (bentukLama) {
        delete baru.corak;
        delete baru.selang;
        delete baru.mulaJam;
        delete baru.hentiJam;
        baru.mulaHari = j.mulaHari ?? 1;
    }

    baru.slot = [...new Set(slot.map(laraskanJam))].sort();
    return baru;
}

export const naikTarafSenarai = (senarai) => (Array.isArray(senarai) ? senarai.map(naikTarafJadual) : []);

/* Salinan jadual sedia ada. Penjanaan id duduk di sini, bukan di dalam
   komponen — memanggil Date.now() dari fail komponen melanggar peraturan
   ketulenan React walaupun ia berlaku di dalam pengendali klik. */
export const salinJadual = (j) => {
    const baru = { ...j, id: 'j' + Date.now(), namaSendiri: false };
    baru.nama = namaAuto(baru);
    return baru;
};

/* Nama automatik supaya jadual baharu tidak lahir sebagai "Jadual 1".
   Pengguna yang menyimpan sepuluh jadual perlu tahu isi setiap satu
   sekali pandang; dia tetap boleh menamakannya semula. */
export function namaAuto(j) {
    const set = j.sets.length === 3
        ? 'Semua set'
        : j.sets.length === 0 ? 'Tiada set' : j.sets.map((s) => 'Set ' + s.slice(3)).join(' + ');
    const bil = waktuJadual(j).length;
    return set + ' — ' + (bil ? bil + ' slot sehari' : 'tiada waktu');
}

/* Ringkasan tetapan untuk dipapar di bawah nama. Nama boleh ditukar
   sesuka hati dan menjadi mengelirukan dua minggu kemudian; baris ini
   sentiasa bercakap benar. */
/* Param `t` (pilihan) — ringkasan ini dirender SETIAP kali kad jadual
   dipapar, jadi ia antara muka, bukan data. Tanpa `t` ia kekal Melayu
   seperti dahulu, jadi pemanggil lama tidak berubah langsung.
   ⚠️ Beza dengan namaAuto() di atas: nama jadual DISIMPAN dan boleh
      ditukar oleh pengguna, jadi ia data — ia sengaja tidak diterjemah. */
export function ringkasJadual(j, t) {
    const T = (kunci, lalai, vars) => (t ? t(kunci, vars) : lalai);
    const keluar = [];
    keluar.push(j.sets.length === 3
        ? T('workspace.schedule.allSets', 'Semua set')
        : j.sets.map((s) => 'Set ' + s.slice(3)).join(' + ') || T('workspace.schedule.noSets', 'Tiada set'));
    if (j.sets.length > 1) {
        keluar.push(j.turutan === 'set'
            ? T('workspace.schedule.interleave', 'Selang seli')
            : T('workspace.schedule.byCard', 'Ikut kad'));
    }
    const waktu = waktuJadual(j);
    /* Sehingga empat waktu disenaraikan penuh; lebih daripada itu barisan
       badge jadi lebih panjang daripada kad yang menaunginya, jadi ia
       diringkaskan kepada kiraan. */
    keluar.push(
        !waktu.length ? T('workspace.schedule.noTimes', 'Tiada waktu')
            : waktu.length <= 4 ? waktu.map(minitKeJam).join(', ')
                : T('workspace.schedule.slotsPerDay', waktu.length + ' slot sehari', { n: waktu.length }),
    );
    /* Satu kunci penuh setiap kes, bukan 'Mula ' + perkataan — susunan
       perkataan berbeza antara bahasa. */
    keluar.push(j.mulaHari === 0
        ? T('workspace.schedule.startToday', 'Mula hari ini')
        : T('workspace.schedule.startTomorrow', 'Mula esok'));
    return keluar;
}

/* Susunan video sebelum masa diletakkan.
     'kad' → A1 A2 A3 B1 B2 B3   (habiskan satu produk dahulu)
     'set' → A1 B1 C1 A2 B2 C2   (selang seli — semua set 1 dahulu)
   Set yang tiada video dilangkau di sini, bukan diberi masa kosong;
   kalau tidak turutan masa akan berlubang. */
export function susunVideo(kad, j) {
    const keluar = [];
    const ada = (p, s) => (p.videos || [])[Number(s.slice(3)) - 1] === 1;
    if (j.turutan === 'set') {
        j.sets.forEach((s) => kad.forEach((p) => { if (ada(p, s)) keluar.push({ p, s }); }));
    } else {
        kad.forEach((p) => j.sets.forEach((s) => { if (ada(p, s)) keluar.push({ p, s }); }));
    }
    return keluar;
}

/* Meletakkan masa pada setiap video: habiskan waktu hari ini dahulu,
   kemudian ulang senarai yang sama pada hari berikutnya.

   Tiada lagi "waktu berhenti malam" yang berasingan — senarai waktu itu
   SENDIRI yang menentukan bila hari tamat. Video ke-32 pada jadual 31
   waktu jatuh pada waktu pertama esok, bukan pukul 3 pagi. */
export function letakMasa(j, jumlah) {
    const hasil = [];
    const slot = waktuJadual(j);
    if (!slot.length) return hasil;

    for (let i = 0; i < jumlah; i++) {
        hasil.push({
            hari: j.mulaHari + Math.floor(i / slot.length),
            minit: slot[i % slot.length],
        });
    }
    return hasil;
}

/* ---------- Akaun ---------------------------------------------------
   Satu "akaun" ialah satu kalendar berasingan dengan LABEL BEBAS yang
   ditaip pengguna. Ia sengaja tidak terikat pada channel: pengguna
   mengasingkannya mengikut caranya sendiri — ikut akaun TikTok, ikut
   platform, atau ikut apa-apa cara lain yang masuk akal baginya.

   Penjanaan id duduk di sini dan bukan di dalam fail komponen atas
   sebab yang sama seperti `salinJadual` di atas: memanggil Date.now()
   dari fail komponen melanggar peraturan ketulenan React. */
export const akaunBaharu = (nama) => ({ id: 'ak' + Date.now().toString(36), nama });

/* Set yang sudah dijadualkan pada kalendar akaun LAIN (bukan yang sedang
   dibuka), beserta nama akaun yang memegangnya:

     { [idProduk]: { set1: 'Akaun 1', set2: 'Akaun 1' } }

   ⛔ Hasilnya MEMBERITAHU, bukan MENGHALANG. Kerana label akaun bebas,
      sistem tidak tahu sama ada akaun lain itu akaun TikTok kedua
      (mengulang video di sana berisiko dikira kandungan berulang) atau
      platform lain seperti Threads (memang selamat, dan memang itu
      tujuannya). Keputusan itu milik pengguna; tugas fungsi ini hanya
      memastikan dia nampak keadaan sebenar sebelum memilih. */
export function setDipegangAkaunLain(susunanSemua, akaunAktif, senaraiAkaun = []) {
    const keluar = {};
    for (const idAkaun in (susunanSemua || {})) {
        if (idAkaun === akaunAktif) continue;
        const nama = senaraiAkaun.find((a) => a.id === idAkaun)?.nama || idAkaun;
        const isian = susunanSemua[idAkaun]?.isian || {};
        for (const k in isian) {
            const e = isian[k];
            /* `e.pid` = bentuk baharu (rujukan sahaja); `e.p?.id` = sokongan
               data lama yang menyimpan salinan penuh objek produk. Tanpa
               `e.pid`, tanda "set dipakai akaun lain" tidak pernah menyala
               untuk susunan yang ditulis selepas 2026-08 (pepijat 4). */
            const id = e?.pid || e?.p?.id;
            if (!id || !e.s) continue;
            if (!keluar[id]) keluar[id] = {};
            /* Akaun PERTAMA yang menempahnya sahaja yang disebut. Kalau dua
               akaun lain memegang set yang sama, menyenaraikan kesemuanya
               dalam satu tooltip kecil lebih mengelirukan daripada membantu
               — yang pengguna perlukan ialah satu tempat untuk pergi. */
            if (!keluar[id][e.s]) keluar[id][e.s] = nama;
        }
    }
    return keluar;
}

/* Kunci satu slot dalam kalendar: TARIKH SEBENAR + waktu, cth
   `2026-09-16_600`. Ia duduk di sini dan bukan dalam fail komponen supaya
   kedua-dua kalendar dan bekasnya membaca formula yang SAMA — kunci yang
   tidak sepadan bermakna video yang diletak tidak akan dijumpai semula.

   ⚠️ Pembaca kunci mesti buang minit dari BELAKANG lalu sambung semula
      selebihnya (`split('_')` → `pop()` → `join('_')`) — tarikh membawa
      tanda sempang, bukan garis bawah, jadi corak itu kekal betul.
      Dahulu `hari` ialah offset (boleh negatif: "-1_600"); corak yang sama
      sudah dipakai sejak itu, jadi tiada pembaca perlu diubah. */
export const kunciSlot = (hari, minit) => `${hari}_${minit}`;

/* Siling keselamatan untuk senarai slot SEBENAR satu hari. 288 = 24 jam
   pada langkah 5 minit, iaitu jadual TERPADAT yang TikTok sendiri boleh
   terima (lihat LANGKAH_MINIT). Ia bukan had reka bentuk — ia hanya
   menghalang template yang tersilap tetapan daripada menjana ribuan nod.

   ⚠️ Ia pernah 96 (langkah 15 minit). Angka itu memotong senyap: template
      rapat akan kehilangan waktu di hujung hari tanpa memberitahu sesiapa. */
export const HAD_SLOT_SEHARI = (24 * 60) / 5;

/* Senarai waktu yang dijana oleh satu template untuk SATU hari.

   `had` kini siling keselamatan sahaja (lalai = HAD_SLOT_SEHARI). Sejarah:
   lalai lama ialah 10 kerana kalendar dahulu memaparkan 10 kotak sahaja
   dan "tumbuh" bila terisi (`slotHariPapar`) — DIBUANG 2026-08-26 atas
   arahan user: pelanggan membina template 31 slot khusus untuk seret
   banyak kad sekali gus, jadi SEMUA waktu template mesti terus kelihatan;
   slot yang tidak kelihatan dibaca sebagai slot hilang. ⚠️ Pengajaran
   aduan 2026-08-13 kekal: keputusan LETAK video mesti sentiasa membaca
   senarai PENUH, bukan senarai yang kebetulan dipapar. */
export function slotHari(j, had = HAD_SLOT_SEHARI) {
    return waktuJadual(j).slice(0, had);
}

/* Waktu template + waktu tambahan pengguna, disusun dan tanpa pendua.
   Waktu tambahan hidup PADA HARI itu sahaja (keputusan user 2026-08-14) —
   template kekal peraturan yang boleh diguna semula, dan hari lain yang
   memakai template sama tidak terjejas langsung. */
export function gabungWaktu(waktuTemplate = [], tambahan = []) {
    if (!tambahan.length) return waktuTemplate;
    return [...new Set([...waktuTemplate, ...tambahan])].sort((a, b) => a - b);
}

/* Slot masa berikutnya yang masih kosong pada satu hari — dipakai bila
   pengguna menyeret produk masuk ke dalam kalendar. Ia mengikut corak
   jadual yang sama, jadi kad yang diletak dengan tangan tetap sebaris
   dengan kad yang dijana automatik. */
export function masaSeterusnya(j, minitDiguna = []) {
    const guna = new Set(minitDiguna);
    const slot = waktuJadual(j);
    if (!slot.length) return 0;

    const kosong = slot.find((t) => !guna.has(t));
    /* Semua waktu penuh — susun di belakang waktu terakhir supaya kad tidak
       bertindih tepat di atas kad sedia ada. */
    return kosong ?? (slot[slot.length - 1] + 30);
}

/* ---------- Seretan PUKAL: kembangkan kad menjadi senarai video ------
   Dipakai apabila beberapa kad ditanda dan diseret sekali gus. Bilangan
   slot yang terisi ditentukan oleh TEMPLATE hari yang diseret
   (keputusan user 2026-08-13), bukan oleh bilangan kad: template itulah
   yang menyatakan set mana hendak dipost (`sets`) dan turutannya
   (`turutan`) — dua kad × template "Semua set" bermakna enam video.

   ⚠️ BEZA daripada `susunVideo`: set yang belum ada video TIDAK
      dilangkau. `susunVideo` menyusun video untuk DIHANTAR, jadi set
      kosong di situ akan meninggalkan lubang masa. Di sini kita menyusun
      TEMPAHAN slot, dan slot memang dibenarkan menunggu videonya
      (lihat `.s-slot--tunggu`). Melangkaunya bermakna kad yang belum
      di-Flow langsung tidak boleh dijadualkan — iaitu masalah asal yang
      membawa kepada kerja ini.

   `sudahDiguna` = { [idProduk]: Set(setKey) } — set yang SUDAH ada dalam
   kalendar ini. Ia ditapis di sini supaya seretan pukal kedua tidak
   menimbunkan pendua di atas kerja pusingan pertama. */
export function kembangIkutTemplate(kadTerpilih = [], j, sudahDiguna = {}) {
    if (!j) return [];
    const keluar = [];
    const tambah = (p, s) => {
        if (sudahDiguna[p.id]?.has?.(s)) return;
        keluar.push({ p, s });
    };
    const sets = j.sets?.length ? j.sets : ['set1'];
    if (j.turutan === 'set') {
        /* Selang seli: semua set 1 dahulu, barulah set 2 — supaya kalau
           slot habis di tengah jalan, setiap produk sekurang-kurangnya
           sudah dapat set pertamanya. */
        sets.forEach((s) => kadTerpilih.forEach((p) => tambah(p, s)));
    } else {
        kadTerpilih.forEach((p) => sets.forEach((s) => tambah(p, s)));
    }
    return keluar;
}

/* Slot kosong bermula dari satu titik jatuh, mengalir ke hari berikutnya.
   Hari yang belum dipilih templatenya dilangkau — hari begitu memang
   tiada kotak slot di skrin, jadi meletakkan video di situ bermakna
   video yang tidak kelihatan di mana-mana.

   Had 30 hari bukan angka rekaan: TikTok sendiri tidak menerima jadual
   lebih jauh daripada itu (HAD_HARI_TIKTOK). */
export function slotKosongDari(hariMula, minitMula, { templates = [], hariTemplate = {}, isian = {}, waktuTambahan = {} } = {}) {
    const keluar = [];
    /* ⛔ Had dikira 30 hari dari HARI INI, BUKAN dari titik jatuh (pepijat 5).
       Dulu `h < hariMula + HAD_HARI_TIKTOK` membenarkan jatuhan pada hari
       ke-25 melimpah sampai hari ke-54 — melepasi had TikTok.
       `hariMula` kini TARIKH SEBENAR; siling dikira semula setiap panggilan
       daripada jam sebenar, jadi ia tidak boleh basi walaupun halaman sudah
       terbuka berhari-hari (lihat seksyen TARIKH SEBENAR di atas). */
    if (!adalahTarikh(hariMula)) return keluar;
    const hujungTarikh = tarikhTambah(tarikhHariIni(), HAD_HARI_TIKTOK);
    /* Perbandingan rentetan pada 'YYYY-MM-DD' = perbandingan tarikh
       (medan tetap, dari besar ke kecil) — tiada penukaran diperlukan. */
    for (let h = hariMula; h <= hujungTarikh; h = tarikhTambah(h, 1)) {
        const t = templates.find((x) => x.id === hariTemplate[h]);
        const tambahan = waktuTambahan[h] || [];
        /* Hari tanpa template masih boleh menerima video kalau pengguna
           menambah waktunya sendiri di situ. */
        if (!t && !tambahan.length) continue;
        /* Senarai PENUH, bukan had paparan — hari ini mesti dihabiskan
           mengikut templatenya sendiri sebelum apa-apa mengalir ke esok. */
        for (const m of gabungWaktu(slotHari(t, HAD_SLOT_SEHARI), tambahan)) {
            if (h === hariMula && m < minitMula) continue;
            if (isian[kunciSlot(h, m)]) continue;
            keluar.push({ hari: h, minit: m });
        }
    }
    return keluar;
}

/* Set berikutnya bagi satu produk yang MEMANG ada video dan belum
   dijadualkan. Mengembalikan null apabila ketiga-tiga sudah masuk —
   pemanggil menggunakannya untuk menolak seretan dengan sebab yang
   jelas, bukan menambah kad pendua secara senyap. */
export function setSeterusnya(p, setDiguna = []) {
    const guna = new Set(setDiguna);
    const ada = ['set1', 'set2', 'set3'].filter((_, i) => (p.videos || [])[i] === 1);
    return ada.find((s) => !guna.has(s)) || null;
}

/* Gabungan susun + letak masa, dikumpulkan mengikut hari. Inilah yang
   menyuap kalendar: setiap kekunci ialah offset hari daripada hari ini,
   nilainya senarai video pada hari tersebut. */
export function binaJadual(kad, j) {
    const video = susunVideo(kad, j);
    const masa = letakMasa(j, video.length);

    /* Slot tetap tanpa satu pun jam bertanda menghasilkan senarai masa
       kosong — video tanpa masa tidak boleh dipaparkan. */
    if (video.length && !masa.length) {
        return { video: [], ikutHari: new Map(), jumlah: 0, tiadaMasa: true, hariAkhir: 0 };
    }

    const ikutHari = new Map();
    video.forEach((v, i) => {
        const t = masa[i];
        if (!ikutHari.has(t.hari)) ikutHari.set(t.hari, []);
        ikutHari.get(t.hari).push({ ...v, minit: t.minit });
    });

    return {
        video,
        masa,
        ikutHari,
        jumlah: video.length,
        hariAkhir: masa.length ? masa[masa.length - 1].hari : 0,
        hariPertama: masa.length ? masa[0].hari : 0,
    };
}
